import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import { downloadMediaUrl } from '../inlineMediaUrl';
import { RevisionEntry, revisionChanges } from './revisionModel';
import { revisionFactLabels, revisionFactText, revisionMakerText } from './revisionFactText';
import { revisionMessages } from './revisionMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
import { trackAssetDownloaded } from '../assetEvents';
/* eslint-enable css-modules/no-unused-class */

interface RevisionRowProps {
  entry: RevisionEntry;
  /** The revision before it (older), for "what changed". */
  previous: RevisionEntry | undefined;
  /** True for the revision on the stage now. */
  isOpen: boolean;
  currentUserId: number | null;
  /** The number of the next revision ("Try again as r5"). */
  nextNumber: number;
  /** False below 700 px and on the demo artist: no Compare. */
  canCompare: boolean;
  isDemo: boolean;
  onOpen: (entry: RevisionEntry) => void;
  onCompare: (entry: RevisionEntry) => void;
  /** Absent: no "Try again" (a music video, an added version). */
  onRemake?: (entry: RevisionEntry) => void;
}

const STATE_CLASS = {
  ready: styles.stateReady,
  failed: styles.stateFailed,
  working: styles.stateWorking,
  fileRemoved: undefined,
} as const;

/**
 * One revision of the history (design d1): the number, the state, who made it and when, what
 * changed against the revision before, and the actions: Open, Download, Compare of a revision with
 * a file; "Try again as rN" of a failed one; a note for a failed one and one whose file is gone.
 */
const RevisionRowComponent: React.FC<RevisionRowProps> = (props) => {
  const { entry, previous, isOpen, currentUserId, nextNumber, canCompare, isDemo } = props;
  const { onOpen, onCompare, onRemake } = props;
  const intl = useIntl();
  const changes = useMemo(() => revisionChanges(entry, previous), [entry, previous]);
  const open = useCallback(() => onOpen(entry), [entry, onOpen]);
  const compare = useCallback(() => onCompare(entry), [entry, onCompare]);
  const remake = useCallback(() => onRemake?.(entry), [entry, onRemake]);
  const stateText = {
    ready: intl.formatMessage(revisionMessages.stateReady),
    failed: intl.formatMessage(revisionMessages.stateFailed),
    working: intl.formatMessage(revisionMessages.stateWorking, { percent: entry.progress }),
    fileRemoved: intl.formatMessage(revisionMessages.stateFileRemoved),
  }[entry.state];
  const when = intl.formatDate(entry.madeAt, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const fileUrl = entry.viewerItem?.fileUrl ?? null;

  return (
    <Panel
      as="li"
      nested
      className={clsx(styles.revItem, isOpen && styles.revItemOpen)}
      aria-current={isOpen ? 'true' : undefined}
      data-testid={`revision-r${entry.number}`}
    >
      <div className={styles.revTop}>
        <span className={styles.revNum}>{intl.formatMessage(revisionMessages.number, { number: entry.number })}</span>
        <span className={clsx(styles.revState, STATE_CLASS[entry.state])}>{stateText}</span>
      </div>
      <span className={styles.revWho}>
        {intl.formatMessage(revisionMessages.makerAt, {
          maker: revisionMakerText(intl, entry, currentUserId),
          date: when,
        })}
      </span>
      {changes.length > 0 && (
        <div className={styles.revChanges}>
          {changes.map((change) => (
            <span key={change.key} className={styles.change}>
              <span>{intl.formatMessage(revisionFactLabels[change.key])}</span>
              <span className={styles.changeFrom}>
                {withBrandWord(revisionFactText(intl, change.key, change.from))}
              </span>
              <b>{withBrandWord(revisionFactText(intl, change.key, change.to))}</b>
            </span>
          ))}
        </div>
      )}
      {entry.state === 'failed' && (
        <span className={styles.goneNote}>{intl.formatMessage(revisionMessages.failedNoFile)}</span>
      )}
      {entry.state === 'fileRemoved' && (
        <span className={styles.goneNote}>{intl.formatMessage(revisionMessages.fileRemoved)}</span>
      )}
      <div className={styles.revActs}>
        {(entry.state === 'ready' || entry.state === 'fileRemoved') && !isOpen && (
          <button type="button" className={styles.linkButton} onClick={open}>
            {intl.formatMessage(revisionMessages.open)}
          </button>
        )}
        {entry.state === 'ready' && fileUrl !== null && (
          <a
            className={styles.linkButton}
            href={downloadMediaUrl(fileUrl)}
            download
            onClick={() => trackAssetDownloaded(undefined, 'viewer')}
          >
            {intl.formatMessage(revisionMessages.download)}
          </a>
        )}
        {entry.state === 'ready' && canCompare && (
          <button type="button" className={styles.linkButton} onClick={compare}>
            {intl.formatMessage(revisionMessages.compare)}
          </button>
        )}
        {entry.state === 'failed' && onRemake !== undefined && (
          <button type="button" className={styles.linkButton} disabled={isDemo} onClick={remake}>
            {intl.formatMessage(revisionMessages.tryAgainAs, { number: nextNumber })}
          </button>
        )}
      </div>
    </Panel>
  );
};

export const RevisionRow = TypedMemo(RevisionRowComponent);
